import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Column } from "react-table";
import { CSVLink } from "react-csv";
import ReactExport from "react-export-excel";
import DayPicker, { DateUtils, LocaleUtils } from "react-day-picker";
import { Dispatch } from "redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "react-day-picker/lib/style.css";

import { State } from "src/state/ducks";
import * as OrderActions from "src/state/ducks/order/actions";

import StyleConfig from "src/constants/StyleConfig";
import {
  DefaultPageProps,
  HistoryProps,
  RevenuePreviewDataProps,
  RevenueReportDataProps,
} from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import HomeButton from "src/components/atoms/button/HomeButton";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import RevenueTable from "src/components/organisms/table/RevenueTable";
import ErrorMessage from "src/components/atoms/form/ErrorMessage";
import AnnotaionText from "src/components/atoms/form/AnnotationText";
import CheckBox from "src/components/atoms/form/CheckBox";
import { OrderItemProps } from "src/firebase/db/Orders";
import { last } from "lodash";

dayjs.locale("ja");

type OwnProps = {};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type DataOrderItemProps = OrderItemProps & {
  user_id: string;
  order_number: string;
  amount: number;
  type: "当日" | "予約";
  bread_id: string;
  payment_datetime: Date;
  pickup_datetime: Date;
};

const RevenueIndexPage = (props: Props) => {
  const [currentDate] = useState(new Date());
  const [from, setFrom] = useState(dayjs().startOf("month").toDate());
  const [to, setTo] = useState(new Date());
  const [rangeTitle, setrangeTitle] = useState(
    `${dayjs().startOf("month").format("YYYY年M月D日")}から${dayjs().format(
      "YYYY年M月D日"
    )}`
  );
  const [isPickupDate, setIsPickupDate] = useState(false);

  const { login_shop_id, isFetching, error, orders, getOrders } = props;
  const today = useMemo(() => new Date(), []);
  const [startOfCalendar, setStartOfCalendar] = useState(
    dayjs().add(-1, "month").startOf("month").toDate()
  );

  // 範囲で検索
  const onSearchByRange = useCallback(() => {
    setrangeTitle(
      `${dayjs(from).format("YYYY年M月D日")}から${dayjs(to).format(
        "YYYY年M月D日"
      )}`
    );
    const endOfCalendar = dayjs(startOfCalendar)
      .add(2, "month")
      .add(-1, "millisecond")
      .toDate();
    getOrders(
      {
        shopId: login_shop_id,
        startDate: dayjs(from).isBefore(startOfCalendar)
          ? from
          : startOfCalendar,
        endDate: dayjs(to).isAfter(endOfCalendar)
          ? to
          : dayjs(today).isBefore(endOfCalendar)
          ? today
          : endOfCalendar,
      },
      isPickupDate
    );
  }, [
    login_shop_id,
    from,
    to,
    isPickupDate,
    startOfCalendar,
    today,
    getOrders,
    setrangeTitle,
  ]);

  // 初期表示：当月の注文データを全件取得
  useEffect(() => {
    onSearchByRange();
  }, [login_shop_id, getOrders, onSearchByRange]);

  // 注文日 or 受取日のチェック(デフォルトは注文日)
  const toggleIsPickupDate = useCallback(() => {
    setIsPickupDate(!isPickupDate);
  }, [isPickupDate, setIsPickupDate]);

  // 現在選択日付のフォーマット
  const curentFormattedDate = useCallback(
    (format: string) => dayjs(currentDate).format(format),
    [currentDate]
  );

  // カレンダー（範囲）用
  const selectedDays = useMemo(() => [from, { from, to }], [from, to]);
  const disabledRange = useMemo(() => [{ after: today }], [today]);
  const initialMonth = useMemo(() => dayjs(today).add(-1, "month").toDate(), [
    today,
  ]);
  const displayRange = useMemo(() => {
    const days: Date[] = [];
    for (
      let day = dayjs(startOfCalendar);
      day.isBefore(dayjs(startOfCalendar).add(2, "month")) &&
      day.isSameOrBefore(dayjs(today));
      day = day.add(1, "day")
    ) {
      days.push(day.toDate());
    }
    return days;
  }, [startOfCalendar, today]);

  // カレンダーの月変更
  const onMonthChange = useCallback((month: Date) => {
    setStartOfCalendar(month);
  }, []);

  // カレンダーの日付選択
  const onClickDayPicker = useCallback(
    (day: Date) => {
      if (dayjs(day).isAfter(today)) {
        return;
      }
      // rangeで取得した日時は12時間ずれる問題があるため検索用に日付は0:00/23:59をセットする
      const range = DateUtils.addDayToRange(day, { from, to });
      if (range.from !== from) {
        setFrom(day);
      }
      if (range.to !== to) {
        setTo(dayjs(day).add(1, "day").add(-1, "millisecond").toDate());
      }
    },
    [from, to, setFrom, setTo, today]
  );

  // テーブル表示用にデータを整形
  const validItems = useMemo(() => {
    return orders
      .filter(({ status }) => status === "succeeded")
      .map((order) => {
        const items = order.items.map((item) => {
          return {
            ...item,
            order_number: order.order_number,
            amount: order.amount,
            user_id: order.user_id,
            bread_id: item.order_bread.bread_id,
            type: item.order_bread.is_reservation ? "予約" : "当日",
            pickup_datetime: order.last_pickup_time.toDate(),
            payment_datetime: order.created_at.toDate(),
          };
        });
        const bags = order.options.map((option) => {
          return {
            ...option,
            name: `レジ袋： ${option.name}`,
            order_number: order.order_number,
            amount: order.amount,
            user_id: order.user_id,
            bread_id: option.id,
            type: "",
            pickup_datetime: order.last_pickup_time.toDate(),
            payment_datetime: order.created_at.toDate(),
          };
        });
        // @ts-ignore
        return items.concat(bags);
      })
      .reduce((prev, curr) => {
        return prev.concat(curr);
      }, [] as DataOrderItemProps[]);
  }, [orders]);

  // カレンダーのスタイル変更用
  const modifiers = useMemo(
    () => ({
      hasOrder: validItems
        .map((item) => {
          const { pickup_datetime, payment_datetime } = item;
          return dayjs(
            isPickupDate ? pickup_datetime : payment_datetime
          ).toDate();
        })
        .filter((day) => {
          return dayjs(day).isAfter(from) && dayjs(day).isBefore(to);
        }),
      isEmpty: displayRange.filter((day) => {
        return !validItems.find((item) => {
          const { pickup_datetime, payment_datetime } = item;
          return dayjs(
            isPickupDate ? pickup_datetime : payment_datetime
          ).isSame(day, "date");
        });
      }),
      start: from,
      end: to,
    }),
    [from, to, validItems, isPickupDate, displayRange]
  );
  const modifiersStyles = {
    hasOrder: {
      backgroundColor: "white",
      color: StyleConfig.COLOR.PRIMARY,
      borderRadius: 0,
    },
    isEmpty: {
      backgroundColor: "#919191",
      color: "white",
      borderRadius: 0,
    },
    start: {
      backgroundColor: StyleConfig.COLOR.PRIMARY,
      color: "white",
      borderRadius: "50% 0 0 50%",
    },
    end: {
      backgroundColor: StyleConfig.COLOR.PRIMARY,
      color: "white",
      borderRadius: "0 50% 50% 0",
    },
    outside: {
      backgroundColor: "#fafafa",
      color: "white",
    },
  };

  // 指定範囲の総売上
  const totalSales = useMemo(() => {
    return orders
      .filter(({ status }) => status === "succeeded")
      .filter((order) => {
        const { created_at, last_pickup_time } = order;
        const datetime = dayjs(
          isPickupDate ? last_pickup_time.toDate() : created_at.toDate()
        );
        return (
          dayjs(datetime).isSameOrAfter(from) &&
          dayjs(datetime).isSameOrBefore(to)
        );
      })
      .reduce((prev, curr) => {
        return prev + curr.amount;
      }, 0);
  }, [orders, from, to, isPickupDate]);

  // 指定範囲の総販売個数
  const totalSoldQuantity = useMemo(() => {
    return validItems
      .filter((item) => {
        const { payment_datetime, pickup_datetime } = item;
        const datetime = dayjs(
          isPickupDate ? pickup_datetime : payment_datetime
        );
        return (
          dayjs(datetime).isSameOrAfter(from) &&
          dayjs(datetime).isSameOrBefore(to)
        );
      })
      .reduce((prev, curr) => {
        return prev + curr.quantity;
      }, 0);
  }, [validItems, from, to, isPickupDate]);

  const previewData: RevenuePreviewDataProps[] = useMemo(() => {
    const rows = validItems
      .filter((item) => {
        const { payment_datetime, pickup_datetime } = item;
        const datetime = dayjs(
          isPickupDate ? pickup_datetime : payment_datetime
        );
        return (
          dayjs(datetime).isSameOrAfter(from) &&
          dayjs(datetime).isSameOrBefore(to)
        );
      })
      .map((item) => {
        const {
          bread_id,
          order_number,
          payment_datetime,
          pickup_datetime,
          name,
          type,
          price,
          quantity,
          amount,
        } = item;
        const datetime = dayjs(
          isPickupDate ? pickup_datetime : payment_datetime
        );
        return {
          date: datetime.format("YYYY/MM/DD"),
          orderNumber: order_number,
          breadId: bread_id,
          name,
          type,
          price,
          quantity,
          amount,
        };
      })
      .sort((a, b) => {
        return dayjs(a.date).diff(b.date);
      });
    // 注文毎にまとめるため重複する値を空に設定
    let lastOrderNumber = null;
    for (let row of rows.reverse()) {
      if (row.orderNumber === lastOrderNumber) {
        row.amount = 0;
      } else {
        lastOrderNumber = row.orderNumber;
      }
    }
    lastOrderNumber = null;
    for (let row of rows.reverse()) {
      if (row.orderNumber === lastOrderNumber) {
        row.orderNumber = "";
        row.date = "";
      } else {
        lastOrderNumber = row.orderNumber;
      }
    }
    return rows;
  }, [validItems, isPickupDate, from, to]);

  // @ts-ignore
  const reportData: RevenueReportDataProps[] = useMemo(() => {
    const rows = validItems
      .filter((item) => {
        const { payment_datetime, pickup_datetime } = item;
        const datetime = dayjs(
          isPickupDate ? pickup_datetime : payment_datetime
        );
        return (
          dayjs(datetime).isSameOrAfter(from) &&
          dayjs(datetime).isSameOrBefore(to)
        );
      })
      .map((item) => {
        const {
          user_id,
          order_number,
          payment_datetime,
          pickup_datetime,
          name,
          type,
          price,
          quantity,
          amount,
        } = item;
        const datetime = dayjs(
          isPickupDate ? pickup_datetime : payment_datetime
        );
        return {
          userId: user_id,
          orderNumber: order_number,
          date: datetime.format("YYYY/MM/DD"),
          time: datetime.format("HH:mm"),
          type,
          name,
          price,
          quantity,
          amount,
        };
      })
      .sort((a, b) => {
        return dayjs(`${a.date}T${a.time}`).diff(`${b.date}T${b.time}`);
      });
    const rowsWithSummary = [];
    let lastOrderNumber = null;
    for (let row of rows.reverse()) {
      if (row.orderNumber !== lastOrderNumber) {
        lastOrderNumber = row.orderNumber;
        rowsWithSummary.push({
          userId: "",
          orderNumber: `【受取番号】 ${row.orderNumber} 計`,
          date: "",
          time: "",
          type: "",
          name: "",
          price: "",
          quantity: "",
          amount: row.amount,
        });
      }
      // @ts-ignore
      row.amount = "";
      rowsWithSummary.push(row);
    }
    return rowsWithSummary.reverse();
  }, [validItems, isPickupDate, from, to]);

  // 売上一覧テーブルのヘッダーデータ
  const previewColumns: Column<RevenuePreviewDataProps>[] = [
    {
      id: "date",
      Header: isPickupDate ? "受取日" : "注文日",
      accessor: "date",
    },
    { id: "orderNumber", Header: "受取番号", accessor: "orderNumber" },
    { id: "name", Header: "商品名", accessor: "name" },
    { id: "type", Header: "種別", accessor: "type" },
    {
      id: "quantity",
      Header: "個数",
      accessor: (d) => d.quantity.toLocaleString(),
    },
    {
      id: "amount",
      Header: "総売上(税込)",
      accessor: (d) => (d.amount > 0 ? d.amount.toLocaleString() : ""),
    },
  ];

  // 売上一覧（詳細）テーブルのヘッダーデータ
  const reportColumns: Column<RevenueReportDataProps>[] = useMemo(
    () =>
      isPickupDate
        ? [
            { id: "orderNumber", Header: "受取番号", accessor: "orderNumber" },
            { id: "userId", Header: "ユーザーID", accessor: "userId" },
            { id: "date", Header: "受取日", accessor: "date" },
            { id: "name", Header: "商品名", accessor: "name" },
            { id: "type", Header: "注文タイプ", accessor: "type" },
            {
              id: "price",
              Header: "単価(税抜)",
              accessor: (d) => (d.price > 0 ? d.price.toLocaleString() : ""),
            },
            {
              id: "quantity",
              Header: "個数",
              accessor: (d) =>
                d.quantity > 0 ? d.quantity.toLocaleString() : "",
            },
            {
              id: "amount",
              Header: "総売上(税込)",
              accessor: (d) => (d.amount > 0 ? d.amount.toLocaleString() : ""),
            },
          ]
        : [
            { id: "orderNumber", Header: "受取番号", accessor: "orderNumber" },
            { id: "userId", Header: "ユーザーID", accessor: "userId" },
            { id: "date", Header: "注文日", accessor: "date" },
            { id: "time", Header: "注文時刻", accessor: "time" },
            { id: "name", Header: "商品名", accessor: "name" },
            { id: "type", Header: "注文タイプ", accessor: "type" },
            {
              id: "price",
              Header: "単価(税抜)",
              accessor: (d) => (d.price > 0 ? d.price.toLocaleString() : ""),
            },
            {
              id: "quantity",
              Header: "個数",
              accessor: (d) =>
                d.quantity > 0 ? d.quantity.toLocaleString() : "",
            },
            {
              id: "amount",
              Header: "総売上(税込)",
              accessor: (d) => (d.amount > 0 ? d.amount.toLocaleString() : ""),
            },
          ],
    [isPickupDate]
  );

  // CSVダウンロード用のヘッダーデータ（詳細）
  const csvDetailedHeaders = useMemo(
    () =>
      reportColumns.map(({ id, Header }) => ({
        label: typeof Header === "string" ? Header.toString() : "",
        key: id || "",
      })),
    [reportColumns]
  );

  // エクセル用のコンポーネント
  const ExcelFile = ReactExport.ExcelFile;
  const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
  const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

  // ファイル名
  const filename = `sacri_${curentFormattedDate("YYYY年M月D日")}_売上詳細`;

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <button onClick={onSearchByRange}>
          <Heading tag="h1" text="売上一覧" />
        </button>
        <LabeledValue>
          <span>{rangeTitle}の総売上</span>
          <span className="value">{totalSales.toLocaleString()}円（税込）</span>
        </LabeledValue>
        <LabeledValue>
          <span>sacriで販売した個数</span>
          <span className="value">{totalSoldQuantity.toLocaleString()}個</span>
        </LabeledValue>
        <AnnotaionText
          color={"secondary"}
          text="※表示金額は総売上からsacriの手数料およびそれに掛かる消費税(10%)を引く前の金額となります。"
        />
        <LabeledCheckbox>
          <span className="label">売上データ設定</span>
          <CheckBox
            isDisabled={false}
            children={"注文した日では無く、お客さまの受取日で売上を表示する"}
            isChecked={isPickupDate}
            onChange={toggleIsPickupDate}
          />
        </LabeledCheckbox>
        <DayPickerContainer>
          <DayPicker
            locale="ja"
            numberOfMonths={2}
            initialMonth={initialMonth}
            selectedDays={selectedDays}
            disabledDays={disabledRange}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            showOutsideDays={false}
            onDayClick={(day) =>
              onClickDayPicker(
                new Date(day.getFullYear(), day.getMonth(), day.getDate())
              )
            }
            onMonthChange={(month) => {
              onMonthChange(
                new Date(month.getFullYear(), month.getMonth(), month.getDate())
              );
            }}
            firstDayOfWeek={1}
            localeUtils={{
              ...LocaleUtils,
              formatMonthTitle: (v) => dayjs(v).format("YYYY年M月"),
              formatWeekdayShort: (v) =>
                ["日", "月", "火", "水", "木", "金", "土"].find(
                  (s, i) => i === v
                ) || "",
            }}
          />
        </DayPickerContainer>
        {/* <div className="range-btn">
          <Button
            isDisabled={false}
            buttonColor="primary"
            clickEvent={onSearchByRange}
            text="選択した範囲の売上を表示する"
          />
        </div> */}

        <Buttons>
          <CSVLink
            data={reportData}
            headers={csvDetailedHeaders}
            filename={filename}
          >
            CSV書き出し
          </CSVLink>
          <ExcelFile filename={filename} element={<button>xls書き出し</button>}>
            <ExcelSheet
              data={reportData}
              name={curentFormattedDate("YYYY年M月D日")}
            >
              {reportColumns.map(
                (
                  column:
                    | Column<RevenuePreviewDataProps>
                    | Column<RevenueReportDataProps>
                ) => (
                  <ExcelColumn
                    label={column.Header}
                    value={column.id}
                    key={column.id}
                  />
                )
              )}
            </ExcelSheet>
          </ExcelFile>
        </Buttons>
        <LabeledValue>
          <span>簡易売上レポート</span>
        </LabeledValue>
        <AnnotaionText
          color={"black"}
          text="※お客さま単位の詳細なレポートは、CSVかxlsファイルをご確認ください"
        />
        {previewData.length > 0 && (
          <RevenueTable data={previewData} columns={previewColumns} />
        )}

        <ErrorMessage>{error?.message}</ErrorMessage>
        <div className="item">
          <HomeButton />
        </div>
      </Container>
    </DefaultLayout>
  );
};

const LabeledValue = styled.div`
  margin-bottom: ${StyleConfig.HELPER.M4};
  font-size: 16px;
  font-weight: bold;
  .value {
    color: ${StyleConfig.COLOR.PRIMARY};
    margin-left: ${StyleConfig.HELPER.M4};
    white-space: nowrap;
  }
`;
const LabeledCheckbox = styled.div`
  margin-top: 10px;
  font-size: 14px;
  line-height: 12px;
  .label {
    font-weight: bold;
  }
  label {
    margin-top: ${StyleConfig.HELPER.M4};
    font-size: 12px;
  }
  input {
    margin-top: ${StyleConfig.HELPER.M4};
    width: 16px;
    height: 16px;
  }
`;
const DayPickerContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;

  .DayPicker {
    width: 100%;
  }

  .DayPicker-wrapper {
    background: #fafafa;
  }

  .DayPicker-Caption {
    text-align: center;

    > div {
      font-weight: bold;
    }
  }

  .DayPicker-Month {
    flex: 1;
  }
`;
const Buttons = styled.div`
  width: 100%;
  margin: 12px 0;
  display: inline-flex;
  justify-content: flex-end;

  a,
  button {
    color: white;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1.5px;
    background-color: #279a40;
    padding: 12px 16px;
    border-radius: 4px;
    text-decoration: none;
    margin-left: 12px;
  }
`;

const Container = styled.div`
  padding: ${StyleConfig.HELPER.M20};
  .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
  .range-btn {
    max-width: 360px;
    margin: 14px auto 10px;
  }
`;

const mapStateToProps = (state: State) => ({
  isFetching: state.order.filterByDate.isFetching,
  orders: state.order.filterByDate.resource,
  error: state.order.filterByDate.apiError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getOrders: (
    props: { shopId: string; startDate: Date; endDate: Date },
    isPickupDate: boolean
  ) => {
    const target = isPickupDate ? "last_pickup_time" : "created_at";
    dispatch(OrderActions.getDayOrders({ ...props, target }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RevenueIndexPage);
